import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { askQuestion, saveContact } from './apiService';

// Configuration from backend
const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || 'Face Track Attendance';
const CHAT_UI_HEADER_BG = process.env.REACT_APP_CHAT_UI_HEADER_BG || '#000000';
const CHAT_UI_HEADER_TEXT = process.env.REACT_APP_CHAT_UI_HEADER_TEXT || '#ffffff';
const CHAT_UI_BUBBLE_USER_BG = process.env.REACT_APP_CHAT_UI_BUBBLE_USER_BG || '#000000';
const CHAT_UI_BUBBLE_USER_TEXT = process.env.REACT_APP_CHAT_UI_BUBBLE_USER_TEXT || '#ffffff';
const CHAT_UI_BUBBLE_BOT_BG = process.env.REACT_APP_CHAT_UI_BUBBLE_BOT_BG || '#f1f0f0';
const CHAT_UI_BUBBLE_BOT_TEXT = process.env.REACT_APP_CHAT_UI_BUBBLE_BOT_TEXT || '#334155';
const CHAT_UI_FONT_FAMILY = process.env.REACT_APP_CHAT_UI_FONT_FAMILY || "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif";
const ENABLE_WEB_SEARCH_DEFAULT = process.env.REACT_APP_ENABLE_WEB_SEARCH_DEFAULT === 'true';
const CHAT_EMBED_ONLY = process.env.REACT_APP_CHAT_EMBED_ONLY === 'true';
const BOT_AVATAR_URL = process.env.REACT_APP_CHAT_BOT_AVATAR_URL || '/chat_bot_logo.jpg';


function App() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(true);
  const [contactInfo, setContactInfo] = useState({ name: '', email: '' });
  const [registeredUser, setRegisteredUser] = useState({ name: '', email: '' });
  const [error, setError] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [useWebSearch] = useState(ENABLE_WEB_SEARCH_DEFAULT);
  const [excludeProductUrls] = useState(false);
  const [isEmbedMode, setIsEmbedMode] = useState(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [unseenStartIndex, setUnseenStartIndex] = useState(null);
  
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const isNearBottomRef = useRef(true);


  const renderTextWithLinks = (text) => {
    const raw = String(text ?? '');
    if (!raw) return null;

    const lines = raw.split('\n');

    const renderLine = (line, lineIdx) => {
      const parts = line.split(/(\s+)/);
      const rendered = parts.map((part, idx) => {
        if (/^\s+$/.test(part)) return part;

        const cleaned = part.replace(/[),.?!:;"'’\]]+$/g, '');
        const trailing = part.slice(cleaned.length);

        const urlMatch = cleaned.match(/^(https?:\/\/[^\s]+)$/i);
        if (urlMatch) {
          const href = urlMatch[1];
          return (
            <React.Fragment key={`${lineIdx}-${idx}`}>
              <a href={href} target="_blank" rel="noreferrer">
                {href}
              </a>
              {trailing}
            </React.Fragment>
          );
        }

        const emailMatch = cleaned.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
        if (emailMatch) {
          const href = `mailto:${cleaned}`;
          return (
            <React.Fragment key={`${lineIdx}-${idx}`}>
              <a href={href} target="_blank" rel="noreferrer">
                {cleaned}
              </a>
              {trailing}
            </React.Fragment>
          );
        }

        return part;
      });

      return rendered;
    };

    return lines.map((line, i) => (
      <React.Fragment key={i}>
        {renderLine(line, i)}
        {i < lines.length - 1 ? <br /> : null}
      </React.Fragment>
    ));
  };

  // Check for embed mode
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const embedParam = urlParams.get('embed') === '1';
    let isInsideIframe = false;
    try {
      isInsideIframe = window.self !== window.top;
    } catch {
      isInsideIframe = true;
    }
    const embed = embedParam || isInsideIframe;
    setIsEmbedMode(embed);

    // If embedded, open by default; otherwise start closed as a corner widget.
    setIsWidgetOpen(embed);
    
    if (CHAT_EMBED_ONLY && !embed) {
      // Show embed-only message
      setMessages([{
        role: 'assistant',
        content: 'This chat is available in embedded mode only. Please use the embed URL.',
        timestamp: new Date().toISOString(),
      }]);
      setShowContactForm(false);
    }
  }, []);

  useEffect(() => {
    if (!isWidgetOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsWidgetOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isWidgetOpen]);

  const updateScrollButtonVisibility = () => {
    const el = chatMessagesRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isNearBottom = distanceFromBottom <= 80;
    isNearBottomRef.current = isNearBottom;
    setShowScrollToBottom(distanceFromBottom > 80);
    if (isNearBottom && unseenStartIndex !== null) {
      setUnseenStartIndex(null);
    }
  };

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
    isNearBottomRef.current = true;
    setShowScrollToBottom(false);
    setUnseenStartIndex(null);
  };

  useEffect(() => {
    if (!messages.length) return;
    const lastMessage = messages[messages.length - 1];
    const shouldAutoScroll = !isWidgetOpen || isNearBottomRef.current || lastMessage?.role === 'user';

    if (shouldAutoScroll) {
      scrollToBottom();
      return;
    }

    if (lastMessage?.role === 'assistant' && unseenStartIndex === null) {
      setUnseenStartIndex(messages.length - 1);
    }
  }, [messages, isWidgetOpen, unseenStartIndex]);

  useEffect(() => {
    updateScrollButtonVisibility();
  }, [messages, isLoading, showContactForm, isWidgetOpen]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError('');

    try {
      const response = await askQuestion(inputMessage.trim(), {
        useWeb: useWebSearch,
        userEmail: registeredUser.email || contactInfo.email,
        conversationHistory: conversationHistory,
        excludeProductUrls: excludeProductUrls,
      });

      const botMessage = {
        role: 'assistant',
        content: response.answer || 'Sorry, I could not process your request.',
        timestamp: new Date().toISOString(),
        sources: response.sources || [],
        sql_used: response.sql_used || false,
        web_used: response.web_used || false,
        products: Array.isArray(response.products) ? response.products : [],
      };

      setMessages(prev => [...prev, botMessage]);
      
      const newHistory = [
        ...conversationHistory,
        { role: 'user', content: userMessage.content },
        { role: 'assistant', content: botMessage.content }
      ];
      setConversationHistory(newHistory.slice(-10)); // Keep last 10 messages

    } catch (err) {
      setError('Failed to get response. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactInfo.name.trim() || !contactInfo.email.trim()) {
      setError('Please fill in all fields');
      return;
    }

    try {
      const name = contactInfo.name.trim();
      const email = contactInfo.email.trim().toLowerCase();
      const result = await saveContact(name, email);
      setRegisteredUser({ name, email });
      setShowContactForm(false);
      
      const welcomeMessage = {
        role: 'assistant',
        content: result?.is_new_user
          ? `Thank you for registering, ${name}! I’m ready to help you. Feel free to ask me anything about ${COMPANY_NAME}.`
          : `Welcome back, ${name}! You can continue chatting. I will use your registered email for order lookups.`,
        timestamp: new Date().toISOString(),
      };
      setMessages([welcomeMessage]);
    } catch (err) {
      setError('Failed to save contact information. Please try again.');
      console.error('Error:', err);
    }
  };

  const formatPrice = (p) => {
    if (p === null || p === undefined || p === '') return '';
    const n = Number(p);
    if (!Number.isFinite(n)) return String(p);
    return `${n.toLocaleString()} USD`;
  };

  const normalizeProduct = (p) => {
    const name = String(p?.name || p?.title || '').trim();
    const url = String(p?.url || p?.product_url || '').trim();
    const imageUrl = String(p?.image_url || p?.image || p?.imageUrl || '').trim();
    const sku = String(p?.sku || '').trim();
    const price = p?.price ?? p?.final_price ?? p?.price_usd ?? '';
    return { name, url, imageUrl, sku, price };
  };

  const renderProducts = (products) => {
    const items = (products || []).map(normalizeProduct).filter((p) => p.name || p.imageUrl || p.url);
    if (!items.length) return null;

    return (
      <div className="product-grid">
        {items.map((p, idx) => (
          <button
            key={`${p.sku || p.name || idx}`}
            type="button"
            className="product-card"
            onClick={() => {
              if (p.url) window.open(p.url, '_blank', 'noreferrer');
            }}
          >
            <div className="product-image-wrap">
              {p.imageUrl ? (
                <img
                  src={p.imageUrl}
                  alt={p.name || 'Product'}
                  className="product-image"
                  loading="lazy"
                />
              ) : (
                <div className="product-image product-image--placeholder">No image</div>
              )}
            </div>
            <div className="product-meta">
              <div className="product-name">{p.name || 'Product'}</div>
              <div className="product-price">{formatPrice(p.price)}</div>
              {!!p.sku && <div className="product-sku">SKU: {p.sku}</div>}
            </div>
          </button>
        ))}
      </div>
    );
  };

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user';
    
    return (
      <div className={`message ${isUser ? 'user' : 'bot'}`}>
        <div className="message-avatar">
          {isUser ? (
            (registeredUser.name || contactInfo.name || 'U').charAt(0).toUpperCase()
          ) : (
            <img
              src={BOT_AVATAR_URL}
              alt={`${COMPANY_NAME} avatar`}
              className="message-avatar-img"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement.textContent = COMPANY_NAME.charAt(0).toUpperCase();
              }}
            />
          )}
        </div>
        <div
          className="message-content"
          style={
            isUser
              ? undefined
              : {
                  backgroundColor: CHAT_UI_BUBBLE_BOT_BG,
                  color: CHAT_UI_BUBBLE_BOT_TEXT,
                }
          }
        >
          <div className="message-text">{renderTextWithLinks(message.content)}</div>
          {!isUser && renderProducts(message.products)}
          <div className="message-metadata">
            <small>
              {message.sql_used && <span className="badge sql-badge">SQL</span>}
              {message.web_used && <span className="badge web-badge">Web</span>}
            </small>
          </div>
        </div>
      </div>
    );
  };

  const ChatPanel = (
    <div className="chat-panel" style={{ fontFamily: CHAT_UI_FONT_FAMILY }}>
      <div className="chat-header" style={{ backgroundColor: CHAT_UI_HEADER_BG, color: CHAT_UI_HEADER_TEXT }}>
        <div className="chat-header-meta">
          <div className="chat-title">{COMPANY_NAME}</div>
          <div className="chat-status-row" aria-label="Online status">
            <span className="chat-status-dot" aria-hidden="true"></span>
            <span className="chat-status-text">Online</span>
          </div>
        </div>
        <button type="button" className="chat-close" onClick={() => setIsWidgetOpen(false)} aria-label="Close chat">
          ×
        </button>
      </div>

      {CHAT_EMBED_ONLY && !isEmbedMode ? (
        <div className="embed-only-container">
          <div className="embed-only-message">
            <h3>Chat Available in Embed Mode Only</h3>
            <p>This chat interface is designed to be embedded in another website.</p>
            <p>Please use the embed URL with <code>?embed=1</code> parameter.</p>
          </div>
        </div>
      ) : showContactForm ? (
        <div className="contact-form">
          <h3>Welcome! Please provide your information to get started</h3>
          {error && <div className="error-message">{error}</div>}
          <form onSubmit={handleContactSubmit}>
            <input
              type="text"
              placeholder="Your Name"
              value={contactInfo.name}
              onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
              required
            />
            <input
              type="email"
              placeholder="Your Email"
              value={contactInfo.email}
              onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
              required
            />
            <button type="submit">Start Chatting</button>
          </form>
        </div>
      ) : (
        <>
          <div
            className="chat-messages"
            ref={chatMessagesRef}
            onScroll={updateScrollButtonVisibility}
          >
            {messages.length === 0 && !isLoading && (
              <div className="message bot">
                <div className="message-avatar">
                  <img
                    src={BOT_AVATAR_URL}
                    alt={`${COMPANY_NAME} avatar`}
                    className="message-avatar-img"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement.textContent = COMPANY_NAME.charAt(0).toUpperCase();
                    }}
                  />
                </div>
                <div className="message-content">
                  Hello! I'm your {COMPANY_NAME} assistant. How can I help you today?
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <React.Fragment key={`${message.timestamp || 'msg'}-${index}`}>
                {unseenStartIndex === index && (
                  <div className="new-messages-divider" role="status" aria-label="New messages">
                    <span>New messages</span>
                  </div>
                )}
                {renderMessage(message, index)}
              </React.Fragment>
            ))}

            {isLoading && (
              <div className="message bot">
                <div className="message-avatar">
                  <img
                    src={BOT_AVATAR_URL}
                    alt={`${COMPANY_NAME} avatar`}
                    className="message-avatar-img"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement.textContent = COMPANY_NAME.charAt(0).toUpperCase();
                    }}
                  />
                </div>
                <div className="typing-indicator">
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="chat-input-container">
            {showScrollToBottom && (
              <button
                type="button"
                className="scroll-to-bottom-btn"
                onClick={() => scrollToBottom('auto')}
                aria-label="Scroll to bottom"
                title="Scroll to bottom"
              >
                ↓
              </button>
            )}
            <form onSubmit={handleSendMessage} className="chat-input-form">
              <input
                type="text"
                className="chat-input"
                placeholder="Type your message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="submit"
                className="chat-send-button"
                disabled={isLoading || !inputMessage.trim()}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Floating launcher (hidden in embed mode) */}
      {!isEmbedMode && (
        <button
          type="button"
          className="chat-launcher"
          onClick={() => setIsWidgetOpen((v) => !v)}
          aria-label="Open chat"
        >
          <img
            src={BOT_AVATAR_URL}
            alt={`${COMPANY_NAME} launcher`}
            className="chat-launcher-img"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <span className="chat-launcher-fallback" aria-hidden="true">💬</span>
        </button>
      )}

      {/* Overlay on mobile when open */}
      {!isEmbedMode && isWidgetOpen && (
        <div className="chat-overlay" role="button" tabIndex={0} onClick={() => setIsWidgetOpen(false)} />
      )}

      {/* Panel */}
      {(isEmbedMode || isWidgetOpen) && (
        <div className={`chat-shell ${isEmbedMode ? 'embed-mode' : ''} ${isWidgetOpen ? 'open' : ''}`}>
          {ChatPanel}
        </div>
      )}
    </>
  );
}

export default App;
